/**
 * Part Change Notification Panel Component
 * AS9100D 8.1.2 Configuration Management - Real-time Part Change Tracking
 * 
 * Displays a collapsible panel showing recent part changes (updates/deletions)
 * for inventory managers to track configuration changes without waiting for toasts.
 * 
 * Features:
 * - Real-time updates via WebSocket events
 * - Collapsible panel with badge count
 * - Color-coded change types (update/delete/create/restore)
 * - Click to navigate to part detail
 * - Clear all functionality
 * - Maximum 10 recent changes retained
 * - IST timezone display
 * 
 * @version 1.0.0
 * @date January 2026 (IST)
 * @author Atlas Dev Team - INT-005 Enhancement
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  ChevronDown, 
  ChevronUp, 
  X, 
  RefreshCw, 
  Trash2, 
  Plus,
  Edit3,
  RotateCcw,
  ExternalLink,
  Shield,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { usePartMasterEvents } from '../hooks/usePartMasterEvents';
import { formatIST } from '../utils/timezone';

// Maximum number of changes to retain
const MAX_CHANGES = 10;

// Change type configuration
const CHANGE_TYPE_CONFIG = {
  updated: {
    icon: Edit3,
    label: 'Updated',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-700',
    badgeVariant: 'outline',
    badgeClass: 'border-blue-500 text-blue-600'
  },
  deleted: {
    icon: Trash2,
    label: 'Deleted',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-700',
    badgeVariant: 'destructive',
    badgeClass: ''
  },
  created: {
    icon: Plus,
    label: 'Created',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-700',
    badgeVariant: 'outline',
    badgeClass: 'border-green-500 text-green-600'
  },
  restored: {
    icon: RotateCcw,
    label: 'Restored',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-700',
    badgeVariant: 'outline',
    badgeClass: 'border-amber-500 text-amber-600'
  }
};

/**
 * Single change item component
 */
const ChangeItem = ({ change, onDismiss, onNavigate }) => {
  const config = CHANGE_TYPE_CONFIG[change.type] || CHANGE_TYPE_CONFIG.updated;
  const Icon = config.icon;
  
  const formattedTime = useMemo(() => {
    if (!change.timestamp) return '';
    try {
      return formatIST(new Date(change.timestamp));
    } catch {
      return change.timestamp;
    }
  }, [change.timestamp]);
  
  return (
    <div 
      className={`p-3 rounded-lg border ${config.bgColor} ${config.borderColor} transition-all hover:shadow-sm`}
      data-testid={`part-change-${change.hpn_compact}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <div className={`p-1.5 rounded-md ${config.bgColor} ${config.textColor}`}>
            <Icon className="w-4 h-4" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => onNavigate(change.hpn_compact)}
                className={`font-medium text-sm ${config.textColor} hover:underline truncate`}
              >
                {change.hpn_compact}
              </button>
              <Badge variant={config.badgeVariant} className={`text-xs ${config.badgeClass}`}>
                {config.label}
              </Badge>
            </div>
            
            {change.changed_fields && change.changed_fields.length > 0 && (
              <p className="text-xs text-gray-500 mt-1 truncate">
                Changed: {change.changed_fields.slice(0, 3).join(', ')}
                {change.changed_fields.length > 3 && '...'}
              </p>
            )}
            
            {change.reason && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                Reason: {change.reason}
              </p>
            )}
            
            <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
              <Clock className="w-3 h-3" />
              <span>{formattedTime}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-gray-400 hover:text-blue-600"
                  onClick={() => onNavigate(change.hpn_compact)}
                >
                  <ExternalLink className="w-3 h-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View Part</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                  onClick={() => onDismiss(change.hpn_compact)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Dismiss</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
};

/**
 * Part Change Notification Panel
 * 
 * @param {Object} props
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.defaultExpanded - Whether panel starts expanded
 * @param {boolean} props.showComplianceInfo - Show AS9100D reference
 */
export const PartChangeNotificationPanel = ({ 
  className = '', 
  defaultExpanded = false,
  showComplianceInfo = true 
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [changes, setChanges] = useState([]);
  const [dismissedHpns, setDismissedHpns] = useState(new Set());
  
  // Use Part Master Events hook
  const { lastPartEvent, eventCount } = usePartMasterEvents({
    enabled: true,
    onPartUpdated: useCallback((hpn_compact, change_type, changed_fields) => {
      setChanges(prev => {
        const newChange = {
          hpn_compact,
          type: 'updated',
          change_type,
          changed_fields,
          timestamp: new Date().toISOString()
        };
        const filtered = prev.filter(c => c.hpn_compact !== hpn_compact);
        return [newChange, ...filtered].slice(0, MAX_CHANGES);
      });
      setDismissedHpns(prev => {
        const newSet = new Set(prev);
        newSet.delete(hpn_compact);
        return newSet;
      });
    }, []),
    onPartDeleted: useCallback((hpn_compact, reason) => {
      setChanges(prev => {
        const newChange = {
          hpn_compact,
          type: 'deleted',
          reason,
          timestamp: new Date().toISOString()
        };
        const filtered = prev.filter(c => c.hpn_compact !== hpn_compact);
        return [newChange, ...filtered].slice(0, MAX_CHANGES);
      });
      setDismissedHpns(prev => {
        const newSet = new Set(prev);
        newSet.delete(hpn_compact);
        return newSet;
      });
    }, []),
    onPartCreated: useCallback((hpn_compact, description) => {
      setChanges(prev => {
        const newChange = {
          hpn_compact,
          type: 'created',
          description,
          timestamp: new Date().toISOString()
        };
        const filtered = prev.filter(c => c.hpn_compact !== hpn_compact);
        return [newChange, ...filtered].slice(0, MAX_CHANGES);
      });
    }, []),
    onPartRestored: useCallback((hpn_compact) => {
      setChanges(prev => {
        const newChange = {
          hpn_compact,
          type: 'restored',
          timestamp: new Date().toISOString()
        };
        const filtered = prev.filter(c => c.hpn_compact !== hpn_compact);
        return [newChange, ...filtered].slice(0, MAX_CHANGES);
      });
      setDismissedHpns(prev => {
        const newSet = new Set(prev);
        newSet.delete(hpn_compact);
        return newSet;
      });
    }, [])
  });
  
  // Filter out dismissed changes
  const visibleChanges = useMemo(() => {
    return changes.filter(c => !dismissedHpns.has(c.hpn_compact));
  }, [changes, dismissedHpns]);
  
  // Unread count (changes that haven't been dismissed)
  const unreadCount = visibleChanges.length;
  
  // Handlers
  const handleDismiss = useCallback((hpn_compact) => {
    setDismissedHpns(prev => new Set([...prev, hpn_compact]));
  }, []);
  
  const handleClearAll = useCallback(() => {
    setDismissedHpns(new Set(changes.map(c => c.hpn_compact)));
  }, [changes]);
  
  const handleNavigate = useCallback((hpn_compact) => {
    navigate(`/parts?panel=${hpn_compact}`);
  }, [navigate]);
  
  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);
  
  // Don't render if no changes
  if (changes.length === 0) {
    return null;
  }
  
  return (
    <Card 
      className={`overflow-hidden transition-all duration-300 ${className}`}
      data-testid="part-change-notification-panel"
    >
      {/* Header */}
      <button
        onClick={toggleExpanded}
        className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-150 transition-colors"
        data-testid="part-change-panel-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <Bell className="w-5 h-5 text-slate-600" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center font-medium">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-sm text-slate-800">
              Part Configuration Changes
            </h3>
            <p className="text-xs text-slate-500">
              {unreadCount === 0 
                ? 'All changes reviewed' 
                : `${unreadCount} recent ${unreadCount === 1 ? 'change' : 'changes'}`
              }
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {showComplianceInfo && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-xs border-blue-300 text-blue-600">
                    <Shield className="w-3 h-3 mr-1" />
                    8.1.2
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="font-medium">AS9100D 8.1.2 - Configuration Management</p>
                  <p className="text-xs mt-1">
                    Tracking part configuration changes ensures identification and control 
                    of physical and functional attributes throughout product lifecycle.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </button>
      
      {/* Expandable Content */}
      {isExpanded && (
        <div className="border-t border-slate-200">
          {/* Actions Bar */}
          {visibleChanges.length > 0 && (
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-500">
                Showing {visibleChanges.length} of {changes.length} changes
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-slate-500 hover:text-red-600"
                onClick={handleClearAll}
                data-testid="clear-all-changes"
              >
                <X className="w-3 h-3 mr-1" />
                Clear All
              </Button>
            </div>
          )}
          
          {/* Changes List */}
          <div className="p-3 space-y-2 max-h-80 overflow-y-auto">
            {visibleChanges.length === 0 ? (
              <div className="py-6 text-center text-slate-400">
                <RefreshCw className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">All changes reviewed</p>
                <p className="text-xs mt-1">New changes will appear here in real-time</p>
              </div>
            ) : (
              visibleChanges.map((change, index) => (
                <ChangeItem
                  key={`${change.hpn_compact}-${change.timestamp}-${index}`}
                  change={change}
                  onDismiss={handleDismiss}
                  onNavigate={handleNavigate}
                />
              ))
            )}
          </div>
          
          {/* Footer */}
          {showComplianceInfo && (
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-100">
              <p className="text-xs text-slate-400 text-center">
                Real-time configuration tracking per AS9100D 8.1.2
              </p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

/**
 * Compact Part Change Badge
 * Shows just a notification badge that can be clicked to see changes
 */
export const PartChangeBadge = ({ onClick, className = '' }) => {
  const { affectedParts } = usePartMasterEvents({ enabled: true });
  const count = affectedParts.length;
  
  if (count === 0) return null;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={onClick}
            className={`relative p-2 rounded-lg hover:bg-slate-100 transition-colors ${className}`}
            data-testid="part-change-badge"
          >
            <Bell className="w-5 h-5 text-slate-600" />
            <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center font-medium animate-pulse">
              {count > 9 ? '9+' : count}
            </span>
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{count} part {count === 1 ? 'change' : 'changes'} detected</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default PartChangeNotificationPanel;
